import {
  CollectionBadgeInfo,
  SectionSummary,
  getCollectionBadgeInfo,
} from "@/services/hymnsApi";
import { scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Globe,
  Layers,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionGroupSlidePanel } from "./SectionGroupSlidePanel";

interface CollectionsGridProps {
  sections: SectionSummary[];
  loading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  /** Called when the user switches between Groupés / Tous so the parent can re-fetch */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Layout mode: 'grid' (default) for full grid, or 'flat' for compact list */
  mode?: "grid" | "flat";
  /** Currently active section code (e.g. 'RN-FR') to highlight in flat mode */
  activeSectionCode?: string;
  /** Optional custom class name */
  className?: string;
}

// Which view mode is active
type ViewMode = "grouped" | "all";

const STORAGE_KEY_VIEW_MODE = "egarizim_grid_view_mode";
const STORAGE_KEY_LANGUAGE = "egarizim_grid_selected_language";

export const CollectionsGrid: React.FC<CollectionsGridProps> = ({
  sections,
  loading = false,
  hasError = false,
  onRetry,
  onViewModeChange,
  mode = "grid",
  activeSectionCode,
  className = "",
}) => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_LANGUAGE);
        if (saved) return saved;
      } catch (e) {
        console.warn("Failed to read selectedLanguage from localStorage", e);
      }
    }
    return "ALL";
  });

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
        if (saved === "grouped" || saved === "all") return saved;
      } catch (e) {
        console.warn("Failed to read viewMode from localStorage", e);
      }
    }
    return "grouped";
  });

  const [openGroup, setOpenGroup] = useState<CollectionBadgeInfo | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY_LANGUAGE, selectedLanguage);
      } catch (e) {
        console.warn("Failed to save selectedLanguage to localStorage", e);
      }
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY_VIEW_MODE, viewMode);
      } catch (e) {
        console.warn("Failed to save viewMode to localStorage", e);
      }
    }
  }, [viewMode]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    onViewModeChange?.(mode);
  };

  // Standard ordered priority codes to match the screenshot's primary layout
  const priorityCodes = [
    "CE-CR",
    "CE-FR",
    "GA",
    "HC-CR",
    "OR",
    "VR-FR",
    "VR-CR",
    "MJ-CR",
    "MJ-FR",
    "RN-CR",
    "RN-FR",
    "RNC-CR",
    "EE",
    "CJ-CR",
    "CJ-FR",
    "RD",
    "TC",
    "CRX",
    "CB-FR",
  ];

  // Map and sort sections
  const mappedCollections = useMemo(() => {
    return sections
      .map((s) => getCollectionBadgeInfo(s))
      .sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        const indexA = priorityCodes.indexOf(a.code);
        const indexB = priorityCodes.indexOf(b.code);
        if (indexA !== -1 && indexB !== -1) {
          if (indexA === indexB) {
            return a.languageCode.localeCompare(b.languageCode);
          }
          return indexA - indexB;
        }
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.code.localeCompare(b.code);
      });
  }, [sections]);

  /**
   * Determine if a collection (group card or individual card) supports a given language
   */
  const collectionSupportsLanguage = (
    collection: CollectionBadgeInfo,
    targetLang: string,
  ): boolean => {
    if (targetLang === "ALL") return true;

    if (!collection.isSectionGroup) {
      if (targetLang === "fr")
        return (
          collection.languageCode === "fr" ||
          collection.languageName.toLowerCase().includes("français") ||
          collection.code.endsWith("-FR")
        );
      if (targetLang === "ht")
        return (
          collection.languageCode === "ht" ||
          collection.languageName.toLowerCase().includes("kreyòl") ||
          collection.code.endsWith("-CR")
        );
      if (targetLang === "en")
        return (
          collection.languageCode === "en" ||
          collection.languageName.toLowerCase().includes("anglais") ||
          collection.languageName.toLowerCase().includes("english")
        );
      if (targetLang === "es")
        return (
          collection.languageCode === "es" ||
          collection.languageName.toLowerCase().includes("espagnol") ||
          collection.languageName.toLowerCase().includes("español")
        );
      return false;
    }

    // Section Group: inspect sub-sections in `sections`
    const nonGroups = sections.filter((s) => !s.is_section_group);
    const related = nonGroups.filter((s) => {
      if (collection.relatedSectionCodes.length > 0) {
        return collection.relatedSectionCodes.includes(s.section_code);
      }
      return (
        s.section_code.startsWith(collection.code + "-") ||
        s.section_code.split("-")[0] === collection.badge
      );
    });

    if (related.length === 0) return true;

    return related.some((s) => {
      const lang = s.language || "";
      const code = s.section_code;
      const name = s.section || "";
      if (targetLang === "fr")
        return (
          lang === "fr" ||
          name.toLowerCase().includes("français") ||
          code.endsWith("-FR")
        );
      if (targetLang === "ht")
        return (
          lang === "ht" ||
          name.toLowerCase().includes("kreyòl") ||
          code.endsWith("-CR")
        );
      if (targetLang === "en")
        return (
          lang === "en" ||
          name.toLowerCase().includes("english") ||
          name.toLowerCase().includes("anglais")
        );
      if (targetLang === "es")
        return (
          lang === "es" ||
          name.toLowerCase().includes("español") ||
          name.toLowerCase().includes("espagnol")
        );
      return false;
    });
  };

  /**
   * Calculate group language/variant count dynamically from section group data or sub-sections
   */
  const getGroupLanguageCount = (collection: CollectionBadgeInfo) => {
    if (collection.relatedSectionCodes.length > 0) {
      return collection.relatedSectionCodes.length;
    }
    const nonGroups = sections.filter((s) => !s.is_section_group);
    const subSections = nonGroups.filter(
      (s) =>
        s.section_code.startsWith(collection.code + "-") ||
        s.section_code.split("-")[0] === collection.badge,
    );
    return subSections.length;
  };

  /**
   * Client-side view-mode filter:
   *  "grouped" → only is_section_group=true entries (group cards with slide panel)
   *  "all"     → only is_section_group=false entries (individual section cards)
   */
  const viewModeFiltered = useMemo(() => {
    if (viewMode === "grouped")
      return mappedCollections.filter((c) => c.isSectionGroup);
    return mappedCollections.filter((c) => !c.isSectionGroup);
  }, [mappedCollections, viewMode]);

  // Dynamic counts for each language (based on view-mode filtered list)

  const filteredCollections = useMemo(() => {
    return viewModeFiltered.filter((c) =>
      collectionSupportsLanguage(c, selectedLanguage),
    );
  }, [viewModeFiltered, selectedLanguage, sections]);

  const handleCardClick = (collection: CollectionBadgeInfo) => {
    if (collection.isSectionGroup) {
      // Open slide panel so user picks language variant
      setOpenGroup(collection);
      return;
    }
    scrollToTop();
    const query = collection.languageCode
      ? `?lang=${encodeURIComponent(collection.languageCode)}`
      : "";
    navigate(
      `/chants-desperance/${encodeURIComponent(collection.code)}${query}`,
    );
  };

  // FLAT MODE: Compact list view designed for sidebars
  if (mode === "flat") {
    return (
      <div
        className={`rounded-2xl sm:rounded-3xl bg-slate-900/95 border border-slate-700/80 shadow-2xl backdrop-blur-xl overflow-hidden flex flex-col ${className}`}
      >
        {/* En-tête Compact */}
        <div className="p-3.5 sm:p-4 border-b border-slate-800 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 flex-shrink-0">
              <Layers className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-white text-xs sm:text-sm truncate">
                Recueils de Cantiques
              </h3>
              <p className="text-[10px] text-slate-400">
                {filteredCollections.length} recueils disponibles
              </p>
            </div>
          </div>
        </div>

        {/* Skeleton Loader pour le mode Flat */}
        {loading && (
          <div className="p-2 space-y-1.5 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2.5 w-full">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex-shrink-0" />
                  <div className="space-y-1 w-3/4">
                    <div className="h-3 bg-slate-800 rounded w-4/5" />
                    <div className="h-2 bg-slate-800/60 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* État d'erreur compact */}
        {!loading && hasError && (
          <div className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-rose-400 mx-auto mb-1.5" />
            <p className="text-slate-300 text-xs mb-2.5">
              Impossible de charger les recueils.
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow transition-all"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Réessayer</span>
              </button>
            )}
          </div>
        )}

        {/* Liste Compacte des Recueils */}
        {!loading && !hasError && (
          <div className="p-2 space-y-1 max-h-[calc(100vh-14rem)] overflow-y-auto divide-y divide-slate-800/40 scrollbar-tiny">
            {filteredCollections.map((collection) => {
              const uniqueKey = `${collection.code}-${collection.languageCode || "all"}`;
              const isGroup = collection.isSectionGroup;
              const isActive =
                Boolean(activeSectionCode) &&
                (activeSectionCode === collection.code ||
                  collection.relatedSectionCodes.includes(
                    activeSectionCode || "",
                  ) ||
                  (collection.badge &&
                    activeSectionCode?.split("-")[0] === collection.badge));

              return (
                <div
                  key={uniqueKey}
                  onClick={() => handleCardClick(collection)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleCardClick(collection);
                    }
                  }}
                  className={`group relative flex items-center justify-between p-2 sm:p-2.5 rounded-xl border transition-all duration-200 cursor-pointer text-left ${
                    isActive
                      ? "bg-sky-950/80 border-sky-400/80 shadow-md shadow-sky-500/20 text-white ring-1 ring-sky-400/40"
                      : "bg-slate-950/50 hover:bg-slate-800/80 border-slate-800/80 hover:border-slate-700 text-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {/* Badge */}
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-serif font-black text-xs transition-colors ${
                        isActive
                          ? "bg-sky-500 text-slate-950 shadow-sm"
                          : "bg-sky-950/70 border border-sky-800/50 text-sky-400 group-hover:text-sky-300 group-hover:bg-sky-900/80"
                      }`}
                    >
                      {collection.badge}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`font-bold text-xs truncate leading-snug ${
                            isActive
                              ? "text-white"
                              : "text-slate-200 group-hover:text-white"
                          }`}
                        >
                          {!isGroup &&
                          collection.languageName &&
                          !collection.name
                            .toLowerCase()
                            .includes(collection.languageName.toLowerCase())
                            ? `${collection.name} (${collection.languageName})`
                            : collection.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {collection.languageName && !isGroup && (
                          <span className="text-[10px] text-sky-300 font-medium">
                            {collection.languageName}
                          </span>
                        )}
                        {isGroup && (
                          <span className="text-[10px] text-amber-300 font-medium">
                            {getGroupLanguageCount(collection)} langues
                          </span>
                        )}
                        {collection.hymnTotal > 0 && !isGroup && (
                          <span className="text-[10px] text-slate-400">
                            • {collection.hymnTotal} chants
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-slate-500 group-hover:text-sky-300 transition-colors">
                    {isGroup ? (
                      <Layers className="w-3.5 h-3.5" />
                    ) : (
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Slide panel for section groups in flat mode */}
        {openGroup && (
          <SectionGroupSlidePanel
            group={openGroup}
            allSections={sections}
            onClose={() => setOpenGroup(null)}
          />
        )}
      </div>
    );
  }

  return (
    <section className="relative w-full py-6 sm:py-12">
      <div className="container mmax-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête en français */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-4 sm:mb-8 md:mb-10 gap-3 sm:gap-6">
          <div className="hidden md:block">
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm font-bold uppercase tracking-widest text-sky-400 mb-1 sm:mb-2">
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
              <span>RECUEILS DE CANTIQUES</span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
              Parcourir les recueils
            </h2>
            <p className="mt-1 sm:mt-3 text-slate-300 text-xs sm:text-base md:text-lg max-w-2xl leading-relaxed">
              Chaque cantique porte un souvenir. Parcourez les recueils
            </p>
          </div>

          {/* Filter controls */}
          <div className="flex flex-col gap-2 w-full md:w-auto">
            {/* View mode toggle: Groupés / Tous */}
            <div className="flex items-center gap-1 bg-slate-900/80 p-1 rounded-xl border border-slate-700/60 backdrop-blur-md">
              <button
                onClick={() => handleViewModeChange("grouped")}
                className={`flex items-center gap-1 px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                  viewMode === "grouped"
                    ? "bg-amber-500 text-black shadow-md shadow-amber-500/20"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                <Layers className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Tous Les Recueils
              </button>
              <button
                onClick={() => handleViewModeChange("all")}
                className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                  viewMode === "all"
                    ? "bg-amber-500 text-black shadow-md shadow-amber-500/20"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                Par langue
              </button>
            </div>

            {/* Language filters */}
            <div className="overflow-x-auto pb-1 sm:pb-0">
              <div className="flex items-center gap-1 sm:gap-2 bg-slate-900/80 p-1 sm:p-1.5 rounded-xl border border-slate-700/60 backdrop-blur-md whitespace-nowrap">
                <button
                  onClick={() => setSelectedLanguage("fr")}
                  className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    selectedLanguage === "fr"
                      ? "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  Français
                </button>
                <button
                  onClick={() => setSelectedLanguage("ht")}
                  className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    selectedLanguage === "ht"
                      ? "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  Kreyòl
                </button>
                <button
                  onClick={() => setSelectedLanguage("ALL")}
                  className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    selectedLanguage === "ALL"
                      ? "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  <Globe className="w-3 h-3 sm:w-3.5 sm:h-3.5 inline mr-1" />{" "}
                  Toutes
                </button>
                <button
                  onClick={() => setSelectedLanguage("en")}
                  className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    selectedLanguage === "en"
                      ? "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setSelectedLanguage("es")}
                  className={`px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                    selectedLanguage === "es"
                      ? "bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  Español
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton Loader animé pour l'API */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className="p-4 sm:p-5 rounded-2xl bg-slate-900/80 border border-slate-800 animate-pulse flex items-center justify-between"
              >
                <div className="flex items-center gap-4 w-full">
                  <div className="w-12 h-10 sm:w-14 sm:h-12 rounded-xl bg-slate-800 flex-shrink-0" />
                  <div className="space-y-2 w-3/4">
                    <div className="h-4 bg-slate-800 rounded w-4/5" />
                    <div className="flex gap-2">
                      <div className="h-3 bg-slate-800 rounded w-16" />
                      <div className="h-3 bg-slate-800 rounded w-12" />
                    </div>
                  </div>
                </div>
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-slate-800 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* Grille de cartes avec clés uniques et affichage systématique de la langue */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-5">
            {filteredCollections.map((collection) => {
              // Clé unique garantie même si GA, EE, OR ou RD apparaissent dans plusieurs langues
              const uniqueKey = `${collection.code}-${collection.languageCode || "all"}`;
              const isGroup = collection.isSectionGroup;

              return (
                <div
                  key={uniqueKey}
                  onClick={() => handleCardClick(collection)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleCardClick(collection);
                    }
                  }}
                  className={`group relative flex items-center justify-between p-3.5 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-slate-900/90 hover:bg-slate-800/95 border border-slate-700/60 hover:border-sky-400/60 shadow-lg sm:shadow-xl hover:shadow-2xl hover:shadow-sky-500/10 backdrop-blur-md transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-0.5`}
                >
                  {/* Surbrillance subtile */}
                  <div className="absolute inset-0 bg-gradient-to-r from-sky-500/0 via-sky-500/5 to-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                  {/* Côté gauche: Badge + Titre + Langue + Total */}
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 pr-2 sm:pr-3 z-10">
                    <span className="font-serif font-black text-xl sm:text-2xl md:text-3xl text-sky-400 group-hover:text-sky-300 tracking-tight flex-shrink-0 min-w-[2.75rem] sm:min-w-[3.25rem] transition-colors">
                      {collection.badge}
                    </span>

                    <div className="min-w-0">
                      <h3 className="font-bold text-white text-sm sm:text-base md:text-lg leading-snug line-clamp-2 group-hover:text-sky-100 transition-colors">
                        {collection.name}
                      </h3>
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 flex-wrap">
                        {collection.languageName && !isGroup && (
                          <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-sky-300 border border-slate-700">
                            {collection.languageName}
                          </span>
                        )}
                        {/* variantes/langues disponibles */}
                        {isGroup &&
                          (() => {
                            const langCount = getGroupLanguageCount(collection);
                            if (langCount === 0) return null;
                            return (
                              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-sky-300 border border-slate-700">
                                <Layers className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                {langCount} langue
                                {langCount !== 1 ? "s" : ""}
                              </span>
                            );
                          })()}
                        {collection.hymnTotal > 0 && !isGroup && (
                          <span className="text-[11px] sm:text-xs text-slate-400 font-medium">
                            {collection.hymnTotal} cantiques
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Icône droite */}
                  <div className="flex-shrink-0 z-10 text-slate-400 group-hover:text-sky-300 group-hover:translate-x-1 transition-all duration-200 p-1 sm:p-2">
                    {isGroup ? (
                      <Layers className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* État d'erreur gracieux */}
        {!loading && hasError && (
          <div className="text-center py-14 px-6 bg-slate-900/80 rounded-3xl border border-rose-500/30 backdrop-blur-md shadow-2xl max-w-xl mx-auto my-4">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-4 text-rose-400">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Impossible de charger les recueils
            </h3>
            <p className="text-slate-300 text-sm mb-6 max-w-md mx-auto leading-relaxed">
              Une erreur est survenue lors de la communication avec la base de
              données. Veuillez vérifier votre connexion Internet ou réessayer.
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm shadow-lg shadow-sky-500/20 transition-all hover:scale-105"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Réessayer</span>
              </button>
            )}
          </div>
        )}

        {/* État vide */}
        {!loading && !hasError && filteredCollections.length === 0 && (
          <div className="text-center py-16 bg-slate-900/50 rounded-2xl border border-slate-800">
            <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300 text-lg font-medium">
              Aucun recueil trouvé pour ce filtre.
            </p>
          </div>
        )}
      </div>

      {/* Slide panel for section groups */}
      {openGroup && (
        <SectionGroupSlidePanel
          group={openGroup}
          allSections={sections}
          onClose={() => setOpenGroup(null)}
        />
      )}
    </section>
  );
};
